import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Icon,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  useMediaQuery,
} from '@mui/material';
import {
  Language, HelpOutline, Summarize,
  Translate, Visibility, School
} from '@mui/icons-material';

// Dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#333333',
    },
    text: {
      primary: '#ffffff',
      secondary: '#e0e0e0',
    },
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#ffb74d',
    },
  },
  typography: {
    h6: { fontWeight: 'bold' },
    body2: { fontSize: '0.875rem' },
  },
});

function ProjectAISync() {
  const isXs = useMediaQuery('(max-width:600px)');
  const isSm = useMediaQuery('(max-width:768px)');
  const isMd = useMediaQuery('(max-width:1024px)');

  const features = [
    { title: 'Vidya', icon: <Language />, description: 'Multilingual AI Assistant for Platform Vishwamitra.' },
    { title: 'Guru', icon: <HelpOutline />, description: 'Multilingual AI Agent that can answer any questions.' },
    { title: 'Saransh', icon: <Summarize />, description: 'Multilingual AI Agent that helps in Summarizing.' },
    { title: 'Anuvadak', icon: <Translate />, description: 'Multilingual AI Assistant for translation.' },
    { title: 'Drishti', icon: <Visibility />, description: 'Multilingual AI Agent that helps in Visualization.' },
    { title: 'Drona', icon: <School />, description: 'Multilingual AI Assistant that prepares for any exam.' },
  ];

  const borderColors = ['#1976d2', '#ffb74d', '#4caf50', '#e91e63', '#f44336', '#9c27b0'];

  const getRadius = () => {
    if (isXs) return 95;
    if (isSm) return 220;
    if (isMd) return 300;
    return 380;
  };

  const getCardSize = () => {
    if (isXs) return 80;
    if (isSm) return 110;
    if (isMd) return 130;
    return 150;
  };

  const [radius, setRadius] = useState(getRadius());
  const [cardSize, setCardSize] = useState(getCardSize());
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    setRadius(getRadius());
    setCardSize(getCardSize());
  }, [isXs, isSm, isMd]);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: {
            xs: '50vh',   // extra-small screens (mobile)
            sm: '70vh',   // small screens (tablets)
            md: '90vh',   // medium screens (small laptops)
            lg: '100vh',   // large screens (desktops)
            xl: '100vh',   // extra-large screens
          },
          backgroundColor: '#000000',
          px: 2,
          position: 'relative',
        }}
      >
        {/* Title */}
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 500,
            textAlign: 'center',
            fontSize: {
              xs: '1rem',
              sm: '1.5rem',
              md: '2rem',
              lg: '2.5rem',
              xl: '3rem',
            },
            backgroundImage: "linear-gradient(45deg, #00bfff, #ff00ff)",
            backgroundClip: "text",
            color: 'transparent',
            letterSpacing: 4,
            // mb: 4,
            transition: 'all 0.3s ease-in-out',
          }}
        >
          AI Sync
        </Typography>

        {/* Cards */}
        {features.map((feature, index) => {
          const angle = (index / features.length) * 360;
          const x = radius * Math.cos((angle * Math.PI) / 180);
          const y = radius * Math.sin((angle * Math.PI) / 180);

          return (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
              }}
            >
              <Card
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  clipPath: 'polygon(50% 0%, 100% 15%, 100% 50%, 85% 100%, 50% 85%, 15% 100%, 0% 50%, 0% 15%)',
                  boxShadow: 3,
                  backgroundColor: '#000000',
                  height: cardSize,
                  width: cardSize,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  border: `2px solid ${borderColors[index % borderColors.length]}`,
                  '&:hover': {
                    transform: 'scale(1.6)',
                    zIndex: 10,
                    borderColor: '#ffffff',
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 1,
                  }}
                >
                  <Icon sx={{ fontSize: 30, color: '#1976d2', mb: 1 }}>
                    {feature.icon}
                  </Icon>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#1E90FF',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      letterSpacing: 1,
                    }}
                  >
                    {feature.title}
                  </Typography>

                  {hoveredCard === index && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.6rem',
                        color: 'text.secondary',
                        textAlign: 'center',
                        mt: 0.5,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>
    </ThemeProvider>
  );
}

export default ProjectAISync;
